import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorCode, ERROR_ICONS } from '../components/Streaks/ErrorPage';

interface FAQItem {
  pergunta: string;
  resposta: string;
  codigo: ErrorCode;
  solucao: string;
}

export const ErrorFAQPage = () => {
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqItems: FAQItem[] = [
    {
      pergunta: "Por que aparece 'Link Expirado'?",
      resposta: "Os links das nossas newsletters expiram após 24 horas da publicação. Isso acontece para garantir que as sequências não sejam forjadas.",
      codigo: ErrorCode.EXPIRED_LINK,
      solucao: "Use sempre o link mais recente enviado por e-mail na edição do dia."
    },
    {
      pergunta: "O que significa 'Erro de Conexão'?",
      resposta: "Este erro ocorre quando há problemas com sua conexão com a internet ou nossos servidores estão temporariamente inacessíveis.",
      codigo: ErrorCode.NETWORK_ERROR,
      solucao: "Verifique sua conexão com a internet e tente novamente em alguns minutos."
    },
    {
      pergunta: "Por que recebo 'E-mail Inválido'?",
      resposta: "Este erro aparece quando o link que você está acessando não é da edição do dia. Lembre-se, ele é seu meio de login no sistema,",
      codigo: ErrorCode.INVALID_EMAIL,
      solucao: "Confira se você está clicando no link correto da edição enviada por e-mail."
    },
    {
      pergunta: "O que fazer quando aparece 'Acesso não autorizado'?",
      resposta: "Este erro indica que você não tem permissão para acessar determinado conteúdo ou sua sessão expirou.",
      codigo: ErrorCode.UNAUTHORIZED,
      solucao: "Tente fazer login novamente ou verifique se você tem acesso ao conteúdo."
    },
    {
      pergunta: "Por que aparece 'Post inválido'?",
      resposta: "Este erro ocorre quando o link do post que você está tentando acessar não existe, foi removido ou você não clicou no link correto da edição enviada por e-mail.",
      codigo: ErrorCode.INVALID_POST,
      solucao: "Acesse o conteúdo diretamente através do link enviado no e-mail mais recente."
    },
    {
      pergunta: "O que significa 'Erro no servidor'?",
      resposta: "Este é um erro técnico que ocorre quando nossos servidores encontram um problema ao processar sua solicitação.",
      codigo: ErrorCode.SERVER_ERROR,
      solucao: "Aguarde alguns minutos e tente novamente. Nossa equipe é automaticamente notificada desses erros."
    },
    {
      pergunta: "Por que aparece 'Erro no banco de dados'?",
      resposta: "Este erro ocorre quando há um problema ao acessar ou salvar informações em nosso banco de dados.",
      codigo: ErrorCode.DATABASE_ERROR,
      solucao: "Aguarde alguns instantes e tente novamente. Se o problema persistir, entre em contato com o suporte."
    },
    {
      pergunta: "O que significa 'Página não encontrada'?",
      resposta: "Este erro aparece quando você tenta acessar uma página que não existe ou foi movida.",
      codigo: ErrorCode.NOT_FOUND,
      solucao: "Verifique se o link está correto ou navegue pelo menu principal."
    },
    {
      pergunta: "Recebi um 'Erro desconhecido', o que fazer?",
      resposta: "Este é um erro genérico que ocorre quando encontramos um problema não categorizado.",
      codigo: ErrorCode.UNKNOWN,
      solucao: "Tente novamente e, se o problema persistir, entre em contato com nosso suporte técnico com o ID do erro."
    },
    // Novos erros específicos do Streaks
    {
      pergunta: "Por que não consigo ver meu histórico de leitura?",
      resposta: "Problemas de autenticação ou carregamento de dados podem impedir a visualização do seu histórico de leitura.",
      codigo: ErrorCode.UNAUTHORIZED,
      solucao: "Verifique se está logado corretamente e atualize a página. Se o problema persistir, entre em contato com o suporte."
    },
    {
      pergunta: "Meu streak não está sendo registrado corretamente",
      resposta: "Podem ocorrer problemas de sincronização ao registrar suas leituras diárias.",
      codigo: ErrorCode.SERVER_ERROR,
      solucao: "Certifique-se de estar usando o link mais recente e ter uma conexão estável. Tente novamente em alguns minutos."
    },
    {
      pergunta: "Erro ao compartilhar meu streak",
      resposta: "Falhas na geração ou compartilhamento da imagem do streak podem ocorrer.",
      codigo: ErrorCode.UNKNOWN,
      solucao: "Verifique sua conexão de internet e tente novamente. Se o problema persistir, entre em contato com o suporte técnico."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 px-4 space-y-8">
      <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
              <img
                src="https://app.thenewscc.com.br/square_logo_favicon.png"
                alt="Coffee Cup"
                className="w-6 h-6"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800 dark:text-gray-200">the news</span>
                <span className="text-sm text-gray-500">FAQ</span>
              </div>
              <div className="text-sm text-gray-500">suporte técnico</div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="text-left space-y-2">
              <h1 className="text-3xl font-bold mb-6">
                <span className="text-yellow-400">FAQ</span> de Erros Comuns
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Antes de conferir ou entrar em contato com o suporte, já tentou acessar por outro navegador? Lembre-se que o foguinho do the news só contabiliza leituras vindas do link mais recente enviado por e-mail na edição do dia.
                <br></br>
                <br></br>
              </p>

              <div className="space-y-4">
                {faqItems.map((item, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleItem(index)}
                      className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{ERROR_ICONS[item.codigo]}</span>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                          {item.pergunta}
                        </h2>
                      </div>
                      <span className="transform transition-transform duration-200 text-xl">
                        {expandedItems.includes(index) ? '−' : '+'}
                      </span>
                    </button>
                    
                    {expandedItems.includes(index) && (
                      <div className="px-4 py-3 bg-white dark:bg-gray-800">
                        <div className="space-y-3 text-gray-600 dark:text-gray-400">
                          <p>{item.resposta}</p>
                          <p className="font-medium">Solução: {item.solucao}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <section className="mt-8">
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-200">
                  <br></br>
                  Ainda com problemas?
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Se você continua enfrentando dificuldades, entre em contato com nossa equipe técnica
                  <a href="https://app.thenewscc.com.br/support?newsletter=thenews" className="text-[#FF3366] ml-2">
                    aqui
                  </a>
                </p>
              </section>
            </div>

            <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
              <Link
                to="/"
                className="text-yellow-400 hover:text-yellow-500 transition-colors"
              >
                ← voltar
              </Link>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Última atualização: 28/02/2025
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 